'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { financialDataService, type NewsArticle } from '@/lib/services/financialDataService';

export default function TrendingNews() {
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const data = await financialDataService.getNewsArticles(6);
        setArticles(data);
        setError(null);
      } catch (err: any) {
        setError(err?.message || 'Failed to load news');
      } finally {
        setLoading(false);
      }
    }
    fetchData();

    // Subscribe to real-time updates
    const unsubscribe = financialDataService.subscribeToNewsArticles((updatedData) => {
      setArticles(updatedData.slice(0, 6));
    }, 6);

    return () => {
      unsubscribe();
    };
  }, []);

  if (loading) {
    return (
      <section className="container mx-auto px-4 py-16 max-w-7xl">
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">Loading news...</div>
        </div>
      </section>);

  }

  if (error) {
    return (
      <section className="container mx-auto px-4 py-16 max-w-7xl">
        <div className="flex items-center justify-center py-12">
          <div className="text-secondary">Error: {error}</div>
        </div>
      </section>);

  }

  const impactColors = {
    high: 'bg-secondary/20 text-secondary border-secondary',
    medium: 'bg-accent/20 text-accent border-accent',
    low: 'bg-primary/20 text-primary border-primary'
  };

  return (
    <section className="container mx-auto px-4 py-16 max-w-7xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-3xl md:text-4xl font-bold mb-2 font-display text-[rgba(83,84,85,1)]">
            Trending News
          </h2>
          <p className="text-muted-foreground">
            Latest updates from trusted financial sources
          </p>
        </div>
        <Link
          href="/news"
          className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors font-medium">

          All News
          <Icon name="ArrowRightIcon" size={20} />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {articles?.map((article) =>
        <article
          key={article.id}
          className="bg-card border border-border rounded-xl overflow-hidden hover:border-primary transition-all group">

            <Link 
              href={article.url && article.url.startsWith('http') ? article.url : `https://${article.url || 'www.moneymine.in/news'}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <div className="relative h-48 overflow-hidden">
                <AppImage
                src={article.image}
                alt={article.alt || `${article.title} - ${article.source} financial news article`}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />

                <div className="absolute top-4 left-4 flex gap-2">
                  <span className="px-3 py-1 bg-primary/90 backdrop-blur-sm text-primary-foreground text-xs font-semibold rounded-full">
                    {article.category}
                  </span>
                  <span
                  className={`px-3 py-1 backdrop-blur-sm text-xs font-semibold rounded-full border ${
                  impactColors[article.impact]}`
                  }>

                    {article.impact.toUpperCase()}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-foreground mb-3 line-clamp-2 group-hover:text-primary transition-colors">
                  {article.title}
                </h3>
                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                  {article.excerpt}
                </p>
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Icon name="NewspaperIcon" size={14} />
                    {article.source}
                  </span>
                  <span>•</span>
                  <span className="flex items-center gap-1">
                    <Icon name="ClockIcon" size={14} />
                    {article.time}
                  </span>
                </div>
              </div>
            </Link>
          </article>
        )}
      </div>
    </section>);

}